/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { NgEventBus } from 'ng-event-bus';
import { MetaData } from 'ng-event-bus/lib/meta-data';
import { of, Subject, Subscription, throwError } from 'rxjs';
import { UtilityService } from '../utility_functions/utility_functions.service';
import { JobsService } from '../jobs/jobs.service';
import { QuotationService } from '../quotations/quotation.service';
import { InvoicesService } from '../invoices/invoices.service';
import { FullScreenLoaderService } from '../FullScreenLoader/full-screen-loader.service';
import { DataCollectorService } from '../data_collector_services/data_collector_service.service';
import { SignInService } from '../signin/sigin.service';
import { map, mergeMap } from 'rxjs/operators';
import { LocalStorageService } from '../localstorage.service';
import { UserService } from 'app/core/user/user.service';
import { DefaultDataService } from '../default_data/default_data.service';
import { ZuperDialogService } from '@zuper/components/zuper-dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/overlay';

@Injectable({
    providedIn: 'root',
})
export class ZuperSdkEventService implements OnDestroy {

    resultFromPreEventListener = new Subject();
    listingPageRefreshSubject = new Subject();
    private _defaultTimeOut = 30000;
    private _childWindow: Window | null = null;
    private _instanceGuid: string | null = null;
    private _eventBusListener: Subscription | any;
    private _modalEventBusListener: Subscription | any;
    private _eventQueue: any[] = [];
    private _routerEventsSub$: Subscription;
    private _pageRefreshInvoke: any = {
        'job_details': (data: any): void => this._jobService.refreshJobDetails.next(data),
        'estimate_details': (data: any): void => this._quotationService.refreshQuotationDetails.next(data),
        'invoice_details': (data: any): void => this._invoiceService.refreshInvoiceDetails.next(data)
    };

    private _preBuildModalIDsForSDK = [
        'job_reschedule',
        'line_item'
    ];

    private _preBuildDrawerIdsForSDK = [
        'new_job',
    ]
    private _preEventRegister: string[] = [];
    private _preEventModalRegister: string[] = [];
    private _currentPageURL: string;

    constructor(
        private _eventBus: NgEventBus,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _utilityService: UtilityService,
        private _jobService: JobsService,
        private _quotationService: QuotationService,
        private _invoiceService: InvoicesService,
        private _fullscreenLoaderService: FullScreenLoaderService,
        private _dataCollectorService: DataCollectorService,
        private _signinService: SignInService,
        private _localStorageService: LocalStorageService,
        private _fullScreenLoader: FullScreenLoaderService,
        private _userService: UserService,
        private _defaultDataService: DefaultDataService,
        private _zuperDialogService: ZuperDialogService
    ) {
        this._eventBusListener = this._eventBus.on('sdk:**').subscribe((res: MetaData) => {
            this.castEventChangesToZDK(res);
        });
        this._modalEventBusListener = this._eventBus.on('modal:**').subscribe((res: MetaData) => {
            this.caseModalEventsChangesToZDK(res);
        });
        this.onWebAppStateChange();
    }

    initializeObjectEventListener(window: Window, instanceGuid: string): void {
        this._childWindow = window;
        this._instanceGuid = instanceGuid;
        if (this._eventQueue.length > 0) {
            // dequeuing event queue
            while (this._eventQueue.length > 0) {
                const message = this._eventQueue.shift();
                this._childWindow.postMessage(
                    { ...message, instanceGuid: this._instanceGuid },
                    window.origin
                );
            }
        };
    }

    castEventChangesToZDK(metaData: MetaData): void {
        const key = metaData.key;
        const keyPatterns = key.split(':');

        const data = {
            id: null,
            key: 'zuper.' + keyPatterns.slice(1).join('.'),
            instanceGuid: this._instanceGuid,
            message: {
                data: metaData.data,
                timestamp: metaData.timestamp
            }
        };
        if (this._childWindow) {
            this._childWindow.postMessage(
                data,
                window.origin
            );
        } else {
            this._eventQueue.push(data);
        }
    }

    caseModalEventsChangesToZDK(metaData: MetaData): void {
        const data = metaData.data;
        const key = metaData.key;
        const keyPatterns = key.split(':');
        const routeData = this._utilityService.collectRouteData(this._activatedRoute.snapshot);

        if (data.id) {
            const payload = {
                id: null,
                key: `zuper.${keyPatterns.slice(1).join('.')}`,
                instanceGuid: `${routeData.page}.${data.id}`,
                message: {
                    data: data,
                    timestamp: metaData.timestamp
                }
            };
            if (this._childWindow) {
                this._childWindow.postMessage(
                    payload,
                    window.origin
                );
            } else {
                this._eventQueue.push(payload);
            }
        }
    }

    onWebAppStateChange(): void {
        this._routerEventsSub$ = this._router.events.subscribe((event: any) => {
            if (event instanceof NavigationStart) {
                this._currentPageURL = this._router.url;
            }
            if (event instanceof NavigationEnd) {
                if (event.urlAfterRedirects === this._currentPageURL) {
                    return;
                }
                this._currentPageURL = event.urlAfterRedirects;
                const routeData = this._utilityService.collectRouteData(this._activatedRoute.snapshot, true);
                const idKey = Object.keys(routeData).find(i => i.includes('_uid'));
                const data = {
                    key: 'zuper.stateChange',
                    instanceGuid: this._instanceGuid,
                    message: {
                        url: event.url,
                        page: routeData.page,
                        id: idKey ? routeData[idKey] : null,
                        module: routeData.module,
                        query_params: this._activatedRoute.snapshot.queryParams
                    }
                };
                if (this._childWindow) {
                    this._childWindow.postMessage(
                        data,
                        window.origin
                    );
                } else {
                    this._eventQueue.push(data);
                }
            }
        });
    }

    onInvokePageRefresh(): void {
        const routeData = this._utilityService.collectRouteData(this._activatedRoute.snapshot);
        let isRefreshed = false;
        if (routeData.page && routeData.page in this._pageRefreshInvoke) {
            this._pageRefreshInvoke[routeData.page]({ refresh: true });
            isRefreshed = true;
        } else if (routeData.page && routeData.page.includes('list')) {
            this.listingPageRefreshSubject.next(true);
            isRefreshed = true;
        }
        if (isRefreshed) {
            // triggering web change event on refresh manually as route change will not emit when redirects to same url
            const idKey = Object.keys(routeData).find(i => i.includes('_uid'));
            this._childWindow.postMessage(
                {
                    key: 'zuper.stateChange',
                    instanceGuid: this._instanceGuid,
                    message: {
                        url: routeData.url,
                        page: routeData.page,
                        id: idKey ? routeData[idKey] : null,
                        module: routeData.module
                    }
                },
                window.origin
            );
        } else {
            throw Error('Current Page cannot be refreshed');
        }
    }

    onInvokeSwitchingAccounts(data: any): void {
        const config = data.params['user.switch'][0];
        let companyConfiguration = {};
        let companySSOConfig = {};
        this._fullScreenLoader.showLoader();
        config['grant_type'] = config['grant_type'] || 'password';
        if (config['grant_type'] === 'password') {
            if (config.company && config.email && config.password) {
                this._dataCollectorService.postData(this._signinService.dcConfigUrl(), {
                    company_name: config.company
                }).pipe(
                    mergeMap((configuration: any) => {
                        companyConfiguration = configuration.config;
                        if (companyConfiguration['dc_api_url']) {
                            const companyName = config.company;
                            return this._dataCollectorService.postData(
                                this._signinService.loginCheckUrl(companyName, companyConfiguration['dc_api_url']),
                                { company_name: companyName }
                            ).pipe(
                                map((sso: any) => {
                                    companySSOConfig = sso;
                                    return of(companySSOConfig);
                                }));
                        }
                    }),
                    mergeMap((sso: any) => {
                        if (sso?.data?.mandate_sso) {
                            throwError({ error: 'Single sign on is mandatory' });
                        }
                        this._localStorageService.setLocalStorage('switching_account', true);
                        return this._dataCollectorService.postData(this._signinService.loginUrl(companyConfiguration['dc_api_url']), {
                            company_login_name: config.company,
                            email: config.email,
                            password: config.password
                        });
                    })).subscribe((event: any) => {
                        if (event.auth_token) {
                            this._signinService.updateCompanyConfiguration(config.company, { config: companyConfiguration });
                            this._signinService.updateSSOConfiguration(companySSOConfig);
                            this.setAuthToken(event.auth_token);
                        }
                    }, (error: any) => {
                        this._localStorageService.removeLocalStorage('switching_account');
                        this._childWindow.postMessage({
                            id: data.id,
                            key: 'user.switch',
                            instanceGuid: data.instanceGuid,
                            result: {
                                error: error.error
                            }
                        }, window.origin);
                        this._fullScreenLoader.hideLoader();
                    });
            } else {
                this.sendUserSwitchErrorMessage(data, 'Error: company, email and password details are required')
            }
        } else if (config['grant_type'] === 'auth_token') {
            if (config['token']) {
                this.setAuthToken(config['token'], false);
            } else {
                this.sendUserSwitchErrorMessage(data, 'Error: authtoken is required for the mentioned grant type')
            }
        }
    }

    sendUserSwitchErrorMessage(data: any, errorMessage: string): void {
        this._childWindow.postMessage({
            id: data.id,
            key: 'user.switch',
            instanceGuid: data.instanceGuid,
            result: {
                error: errorMessage
            }
        }, window.origin);
        this._fullScreenLoader.hideLoader();
    }

    setAuthToken(authToken: string, loadDefaultData = true) {
        this._localStorageService.removeLocalStorage('auth_token');
        this._localStorageService.setLocalStorage('auth_token', authToken);
        this._localStorageService.removeLocalStorage('switching_account');
        this._userService.logout$.next(true);
        if (loadDefaultData) {
            this._defaultDataService.getDefaultdata().subscribe((res) => {
                window.location.reload();
            });
        } else {
            window.location.reload();
        }
    }

    ngOnDestroy(): void {
        if (this._eventBusListener) {
            this._eventBusListener.unsubscribe();
        }
        if (this._routerEventsSub$) {
            this._routerEventsSub$.unsubscribe();
        }
    }

    castPreBuildFormPatch(data: any): void {
        Object.keys(data).forEach((key: string) => {
            const serializedKey = key.replace('.', ':');
            this._eventBus.cast('patchForm:' + serializedKey, data[key]);
        });
    }

    castPreBuildFormModalPatch(data: any, instanceId: string, event: string): void {
        Object.keys(data).forEach((key: string) => {
            this._eventBus.cast(`patchForm:${instanceId}:${event}`, {
                controlName: key,
                [event === 'prop' ? 'config' : 'value']: data[key]
            });
        });
    }

    casePreBuildModalClose(instanceId: string): void{
        this._eventBus.cast(`${instanceId}:close`);
    }
    
    castPreBuildDrawerPatchForSDK(data: any, instanceId: string, event: string): void {

        const param = Object.keys(data)[0];
        const paramData = data[param];
        Object.keys(paramData).forEach((key: string) => {
            this._eventBus.cast(`patchForm:${param}:${event}`, {
                controlName: key,
                [event === 'prop' ? 'config' : 'value']: paramData[key]
            });
        });
    }

    checkDataKeyFormat(page: string, data: any): boolean {
        return Object.keys(data).every(key =>
            key.includes(page)
        );
    }

    isPreBuildModalIdAvailable(modalId: string | string[]): boolean {
        return this._preBuildModalIDsForSDK.some((id: string) => modalId.includes(id));
    }

    isPreBuildDrawerIdAvailable(params: string | string[]): boolean {
        const drawerId = Object.keys(params)[0];
        if(!drawerId) {
            return false;
        }
        return this._preBuildDrawerIdsForSDK.some((id: string) => drawerId.includes(id));
    }

    registerPreEventHandler(key: string): void {
        key = key.split('pre_event.')[1];
        this._preEventRegister.push(key);
    }

    checkForPreEventHandler(key: string): boolean {
        return this._preEventRegister.includes(key);
    }

    handleSdkListenerResponse(data: any): void {
        if (!data || typeof data != 'object') {
            return;
        }
        const key = Object.keys(data)[0];
        const keyData = key.split('.');
        if (keyData[0] === 'pre_event' && (keyData[1] === 'accept' || keyData[1] === 'reject')) {
            this.resultFromPreEventListener.next({
                event_type: 'pre_event',
                response: keyData[1],
                ...data[key]
            });
        }
    }

    castPreEventListener(key: string, payload: any, instanceId?: string): Promise<boolean> {
        const data = {
            id: null,
            key: 'zuper.pre_event.' + key,
            instanceGuid: instanceId || this._instanceGuid,
            message: {
                data: payload,
            }
        };
        if (this._childWindow) {
            return new Promise((resolve, reject) => {
                this._fullscreenLoaderService.showLoader();
                const preEventListener$ = this.resultFromPreEventListener.subscribe(((res: any) => {
                    if (res.event_type === 'pre_event' && res.field === key) {
                        if (res.response === 'accept') {
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                        this._fullscreenLoaderService.hideLoader();
                    }
                }));
                this._childWindow.postMessage(
                    data,
                    window.origin
                );
                setTimeout(() => {
                    preEventListener$.unsubscribe();
                    resolve(true);
                    this._fullscreenLoaderService.hideLoader();
                }, this._defaultTimeOut);

            });
        }
    }

    openCustomWithPreEventListener<T, D = any, R = any>(component: ComponentType<T>, config?: any, values?: any): Promise<MatDialogRef<T, R>>
    {   
        if(config.dialogId && this.checkForPreEventHandler(config.dialogId)) {
            return this.castPreEventListener(config.dialogId, values).then((canProceed: boolean) => {
                if(!canProceed) {
                    return null;
                }
                return this._zuperDialogService.openCustom(component, config);
            });
        } else {
            return Promise.resolve(this._zuperDialogService.openCustom(component, config));
        }
    }

}
